import { apiFetch } from '../lib/apiFetch'
import { logger } from '../lib/logger'

export type VisioClientLogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface VisioClientLogContext {
  room: string
  guestId?: string
}

export interface VisioClientLogEntry {
  ts_client: string
  session: string
  room: string
  event: string
  data: Record<string, unknown>
  level: VisioClientLogLevel
  guest_id?: string
}

interface VisioClientLoggerOptions {
  post?: (entries: VisioClientLogEntry[], keepalive: boolean, isGuest: boolean) => Promise<boolean>
  beacon?: (entries: VisioClientLogEntry[]) => boolean
  now?: () => Date
  session?: string
  flushIntervalMs?: number
  flushSize?: number
  eventTarget?: Pick<Window, 'addEventListener' | 'removeEventListener'>
}

const ENDPOINT = '/api/visio/client-logs'
const FLUSH_INTERVAL_MS = 5_000
const FLUSH_SIZE = 50
const MAX_BUFFER_SIZE = 200
const MAX_EVENT_CHARS = 64
const MAX_STRING_CHARS = 512
const MAX_COLLECTION_ITEMS = 50
const MAX_DEPTH = 5

const EVENT_ALIASES: Record<string, string> = {
  'room.joined': 'join',
  'room.left': 'leave',
  'room.reconnecting': 'lk_reconnecting',
  'room.reconnected': 'lk_reconnected',
  'room.disconnected': 'lk_disconnected',
  'room.error': 'lk_error',
  'mic.off': 'mute_mic',
  'mic.on': 'unmute_mic',
  'camera.on': 'cam_on',
  'camera.off': 'cam_off',
  'screenshare.on': 'share_start',
  'screenshare.off': 'share_stop',
  'device.switch': 'device_change',
  'device.error': 'media_device_error',
  'vbg.error': 'vbg_error',
}

const SENSITIVE_KEY_FRAGMENTS = [
  'authorization',
  'password',
  'passwd',
  'token',
  'invite',
  'secret',
  'chat',
  'message',
  'content',
  'customsrc',
  'url',
]

/**
 * Dedicated, best-effort JSONL client logger for visio diagnostics.
 * It intentionally never retries or surfaces transport errors to the UI.
 */
export class VisioClientLogger {
  private buffer: VisioClientLogEntry[] = []
  private context: VisioClientLogContext | null = null
  private readonly post: NonNullable<VisioClientLoggerOptions['post']>
  private readonly beacon: NonNullable<VisioClientLoggerOptions['beacon']>
  private readonly now: () => Date
  private readonly session: string
  private readonly flushSize: number
  private readonly eventTarget?: VisioClientLoggerOptions['eventTarget']
  private readonly interval: ReturnType<typeof setInterval>
  private vbgActive = false
  private unloadLogged = false

  private readonly onPageHide = () => this.flushOnUnload('pagehide')
  private readonly onBeforeUnload = () => this.flushOnUnload('beforeunload')

  constructor(options: VisioClientLoggerOptions = {}) {
    this.post = options.post ?? postEntries
    this.beacon = options.beacon ?? beaconEntries
    this.now = options.now ?? (() => new Date())
    this.session = options.session ?? logger.getSession()
    this.flushSize = options.flushSize ?? FLUSH_SIZE
    this.eventTarget = options.eventTarget
      ?? (typeof window !== 'undefined' ? window : undefined)
    this.interval = setInterval(() => {
      void this.flush(false)
    }, options.flushIntervalMs ?? FLUSH_INTERVAL_MS)
    this.eventTarget?.addEventListener('pagehide', this.onPageHide)
    this.eventTarget?.addEventListener('beforeunload', this.onBeforeUnload)
  }

  configure(context: VisioClientLogContext): void {
    const room = takeChars(context.room, 64)
    if (this.context?.room !== room) this.vbgActive = false
    this.context = {
      room,
      guestId: context.guestId ? takeChars(context.guestId, 64) : undefined,
    }
    this.unloadLogged = false
  }

  log(
    context: VisioClientLogContext,
    sourceEvent: string,
    data: Record<string, unknown> = {},
    level?: VisioClientLogLevel,
  ): void {
    try {
      this.configure(context)
      if (sourceEvent === 'stats.tick') return
      const event = this.normalizeEvent(sourceEvent, data)
      if (!event) return
      this.enqueue(event, data, level ?? levelForEvent(event))
    } catch {
      // Logging is deliberately fire-and-forget.
    }
  }

  async flush(keepalive = false): Promise<void> {
    if (this.buffer.length === 0) return
    const entries = this.buffer.splice(0, this.buffer.length)
    const isGuest = entries.every(entry => Boolean(entry.guest_id))
    try {
      await this.post(entries, keepalive, isGuest)
    } catch {
      // Drop the batch: diagnostics must never affect the conference UI.
    }
  }

  destroy(): void {
    clearInterval(this.interval)
    this.eventTarget?.removeEventListener('pagehide', this.onPageHide)
    this.eventTarget?.removeEventListener('beforeunload', this.onBeforeUnload)
  }

  pendingCount(): number {
    return this.buffer.length
  }

  private enqueue(
    event: string,
    data: Record<string, unknown>,
    level: VisioClientLogLevel,
  ): void {
    if (!this.context?.room) return
    const entry: VisioClientLogEntry = {
      ts_client: this.now().toISOString(),
      session: takeChars(this.session, 128),
      room: this.context.room,
      event: takeChars(event, MAX_EVENT_CHARS),
      data: sanitizeRecord(data),
      level,
    }
    if (this.context.guestId) entry.guest_id = this.context.guestId
    this.buffer.push(entry)
    if (this.buffer.length > MAX_BUFFER_SIZE) {
      this.buffer.splice(0, this.buffer.length - MAX_BUFFER_SIZE)
    }
    if (this.buffer.length >= this.flushSize) {
      void this.flush(false)
    }
  }

  private flushOnUnload(reason: 'pagehide' | 'beforeunload'): void {
    try {
      if (this.context && !this.unloadLogged) {
        this.unloadLogged = true
        this.enqueue('leave', { reason }, 'info')
        this.enqueue('lk_disconnected', { reason, abrupt: true }, 'warn')
      }
      if (this.buffer.length === 0) return
      const entries = this.buffer.splice(0, this.buffer.length)
      const isGuest = entries.every(entry => Boolean(entry.guest_id))

      // sendBeacon cannot carry a Bearer header. It is therefore used for the
      // verified guest path; authenticated users use an auth-aware keepalive
      // fetch so the server can force the Keycloak subject instead of trusting
      // a client-provided identity.
      if (isGuest && this.beacon(entries)) return
      void this.post(entries, true, isGuest).catch(() => {})
    } catch {
      // Unload handlers must never block navigation.
    }
  }

  private normalizeEvent(sourceEvent: string, data: Record<string, unknown>): string | null {
    if (sourceEvent === 'vbg.init') {
      this.vbgActive = data.mode !== 'none'
      return this.vbgActive ? 'vbg_on' : null
    }
    if (sourceEvent === 'vbg.change') {
      const activeId = typeof data.activeId === 'string' ? data.activeId : ''
      if (activeId === 'none') {
        this.vbgActive = false
        return 'vbg_off'
      }
      const event = this.vbgActive ? 'vbg_change' : 'vbg_on'
      this.vbgActive = true
      return event
    }
    if (sourceEvent === 'vbg.ready' || sourceEvent === 'vbg.first-mask') {
      return this.vbgActive ? 'vbg_change' : null
    }
    return EVENT_ALIASES[sourceEvent] ?? sanitizeEventName(sourceEvent)
  }
}

async function postEntries(
  entries: VisioClientLogEntry[],
  keepalive: boolean,
  isGuest: boolean,
): Promise<boolean> {
  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(entries),
    keepalive,
  }
  const response = isGuest ? await fetch(ENDPOINT, init) : await apiFetch(ENDPOINT, init)
  return response.ok
}

function beaconEntries(entries: VisioClientLogEntry[]): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false
  const payload = new Blob([JSON.stringify(entries)], { type: 'application/json' })
  return navigator.sendBeacon(ENDPOINT, payload)
}

function sanitizeEventName(event: string): string {
  const safe = Array.from(event)
    .slice(0, MAX_EVENT_CHARS)
    .filter(character => /[A-Za-z0-9._-]/.test(character))
    .join('')
  return safe || 'client_event'
}

function levelForEvent(event: string): VisioClientLogLevel {
  if (event.includes('error') || event === 'lk_error') return 'error'
  if (event.includes('degraded') || event.includes('reconnecting') || event.includes('disconnected')) return 'warn'
  return 'info'
}

function sanitizeRecord(data: Record<string, unknown>): Record<string, unknown> {
  return sanitizeValue(data, 0) as Record<string, unknown>
}

function sanitizeValue(value: unknown, depth: number): unknown {
  if (depth >= MAX_DEPTH) return '[truncated]'
  if (typeof value === 'string') return takeChars(value, MAX_STRING_CHARS)
  if (Array.isArray(value)) {
    return value.slice(0, MAX_COLLECTION_ITEMS).map(item => sanitizeValue(item, depth + 1))
  }
  if (value && typeof value === 'object') {
    const clean: Record<string, unknown> = {}
    for (const [rawKey, item] of Object.entries(value).slice(0, MAX_COLLECTION_ITEMS)) {
      const key = takeChars(rawKey, 64)
      const normalized = key.toLowerCase()
      clean[key] = normalized === 'user' || normalized === 'ua' || normalized === 'ts_server'
        || SENSITIVE_KEY_FRAGMENTS.some(fragment => normalized.includes(fragment))
        ? '[redacted]'
        : sanitizeValue(item, depth + 1)
    }
    return clean
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'boolean' || value === null) return value
  return String(value).slice(0, MAX_STRING_CHARS)
}

function takeChars(value: string, maxChars: number): string {
  return Array.from(value).slice(0, maxChars).join('')
}

let singleton: VisioClientLogger | null = null

function getVisioClientLogger(): VisioClientLogger {
  singleton ??= new VisioClientLogger()
  return singleton
}

export function logVisioClientEvent(
  room: string,
  event: string,
  data?: Record<string, unknown>,
  options?: { guestId?: string; level?: VisioClientLogLevel },
): void {
  getVisioClientLogger().log(
    { room, guestId: options?.guestId },
    event,
    data,
    options?.level,
  )
}
